import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Box } from '@mui/material';
import React, { useState } from 'react';

import GameService from '../../../../../services/game.service';
import EditFolderTreeView from '../../../edits/treeview';

const GameExportToEdits = ({ open, onClose, tagList, isTeams, t }) => {
    const [curEdit, setCurEdit] = useState(null);

    const getPeriod = (id) => {
        return id === 1 ? 'H1' : id === 2 ? 'H2' : 'OT';
    };

    // Convert time string (HH:MM:SS) to total seconds
    const timeToSeconds = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string') return 0;
        const parts = timeStr.split(':');
        if (parts.length >= 3) {
            return parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
        } else if (parts.length >= 2) {
            return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
        }
        return 0;
    };

    // Sort by start time first
    tagList.sort((a, b) => {
        const timeA = new Date(`1970-01-01T${a.player_tag_start_time}Z`);
        const timeB = new Date(`1970-01-01T${b.player_tag_start_time}Z`);
        return timeA - timeB;
    });

    // Find the first clip start time for each game AND period (baseline for game time calculation)
    // Key format: "gameId_period"
    const periodBaselines = {};
    tagList.forEach((item) => {
        const gameId = item.game_id;
        const period = item.period;
        const key = `${gameId}_${period}`;
        const startTime = item.player_tag_start_time || item.team_tag_start_time || item.start_time;
        const startSeconds = timeToSeconds(startTime);
        if (periodBaselines[key] === undefined || startSeconds < periodBaselines[key]) {
            periodBaselines[key] = startSeconds;
        }
    });

    // Get game time - use API-provided time_in_game if available, otherwise calculate
    // H1: game_time = (start - baseline + 5) / 60, starting from 0'
    // H2: game_time = 45 + (start - baseline + 5) / 60
    // OT: game_time = 90 + (start - baseline + 5) / 60
    const getTimeInGame = (item) => {
        // Use API-provided time_in_game if available
        if (item.time_in_game !== undefined && item.time_in_game !== null && item.time_in_game !== '') {
            return parseInt(item.time_in_game, 10) || 0;
        }

        // Fallback: calculate from start_time relative to period baseline
        const startTime = item.player_tag_start_time || item.team_tag_start_time || item.start_time;
        const startSeconds = timeToSeconds(startTime);
        const gameId = item.game_id;
        const period = item.period;
        const key = `${gameId}_${period}`;
        const baseline = periodBaselines[key] || 0;

        const relativeSeconds = startSeconds - baseline + 5;

        if (period === 1 || period === 'H1') {
            // H1: start from 0'
            return Math.max(0, Math.floor(relativeSeconds / 60));
        } else if (period === 2 || period === 'H2') {
            // H2: start from 45'
            return 45 + Math.floor(relativeSeconds / 60);
        } else {
            // OT: start from 90'
            return 90 + Math.floor(relativeSeconds / 60);
        }
    };

    const getName = (item) => {
        // Handle both singular and plural field name formats
        const playerName = item.player_names || item.player_name || '';
        const actionName = item.action_names || item.action_name || '';
        const actionTypeName = item.action_type_names || item.action_type_name || '';
        const actionResultName = item.action_result_names || item.action_result_name || '';
        return `${getPeriod(item.period)} - ${getTimeInGame(item)}' - ${playerName} - ${actionName} - ${actionTypeName} - ${actionResultName}`;
    };

    const handleSave = async () => {
        if (curEdit.type === 'edit') {
            let bigSort = 0;

            await GameService.getBiggestSortNumber('Clip', curEdit.id ?? 0).then((res) => {
                bigSort = res['biggest_order_num'] === null ? 0 : res['biggest_order_num'];
            });

            const newList = tagList.map((item, index) => {
                return {
                    start_time: isTeams ? item.team_tag_start_time : item.player_tag_start_time,
                    end_time: isTeams ? item.team_tag_end_time : item.player_tag_end_time,
                    edit_id: curEdit.id,
                    sort: bigSort + index + 1,
                    game_id: item.game_id,
                    name: getName(item)
                };
            });
            await GameService.addNewEditClips({ id: curEdit.id, rows: newList });
            onClose();
        } else window.alert('You selected folder. Please select edit to save clips.');
    };

    return (
        <Dialog open={open} onClose={onClose} scroll="paper" maxWidth="lg">
            <DialogTitle>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '16px', fontWeight: 600, color: '#1a1b1d' }}>Export to My Edits</Typography>
            </DialogTitle>
            <DialogContent dividers={true} style={{ display: 'flex', overflowY: 'hidden' }}>
                <EditFolderTreeView t={t} setEdit={setCurEdit} isMain={false} entireHeight="390px" treeHeight="85%" />
                <Box sx={{ overflowY: 'auto', maxHeight: '390px', width: '600px', paddingLeft: '16px' }}>
                    <Box sx={{ margin: '0 4px 8px 0', width: 'calc(100% - 4px)' }}>
                        {tagList.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    gap: '8px',
                                    padding: '4px',
                                    border: '1px solid #e8e8e8',
                                    borderRadius: '8px'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box sx={{ background: '#5C738E', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>{index + 1}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d' }}>
                                            {isTeams ? `${getPeriod(item.period)} - ${getTimeInGame(item)}' - ${item.player_names || item.player_name || ''}` : getName(item)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 500, color: '#1a1b1d' }}>
                                                {`${item.home_team_name} VS ${item.away_team_name}`}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '12px', fontWeight: 500, color: '#1a1b1d' }}>{item.game_date}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: '4px', width: '80px' }}>
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d' }}>{item.player_tag_start_time}</Typography>
                                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d' }}>{item.player_tag_end_time}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                <Button variant="outlined" onClick={() => handleSave()}>
                    Export
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GameExportToEdits;
